import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import SellerCard from "../components/SellerCard";
import BuyerCard from "../components/BuyerCard";
import ActivityStrip from "../components/ActivityStrip";
import RateTicker from "../components/RateTicker";
import { PopBattleStatusHero, PopBattleRewardsCard, SeasonEndedNotice } from "../components/PopBattleSeason";
import PopBattlePointChart from "../components/PopBattlePointChart";
import PopBattleLevelChart from "../components/PopBattleLevelChart";
import ForumFeed from "../components/ForumFeed";
import { querySellers } from "../services/sellers";
import { queryBuyers } from "../services/buyers";
import { isDemoMode } from "../services/mode";
import { REGIONS, SEGMENTS, TIERS, RESERVE_TIERS,
  LANGUAGES } from "../services/constants";
import { useGame } from "../services/gameContext";
import GamePicker from "../components/GamePicker";
import "./Directory.css";

const SELLER_SORTS = [
  { key: "newest",       label: "Newest" },
  { key: "cheapestMix",  label: "Cheapest Mix" },
  { key: "topRated",     label: "Top rated" },
  { key: "mostReviewed", label: "Most reviewed" },
  { key: "highestStock", label: "Highest stock" },
];
const BUYER_SORTS = [
  { key: "newest",         label: "Newest" },
  { key: "biggestBudget",  label: "Biggest budget" },
];
const URGENCIES = [
  { key: "now",   label: "Urgent · now" },
  { key: "24h",   label: "Within 24h" },
  { key: "week",  label: "This week" },
  { key: "month", label: "This month" },
];

export default function Directory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const role = searchParams.get("role") === "buyer" ? "buyer" : "seller";
  const setRole = (r) => {
    const next = new URLSearchParams(searchParams);
    if (r === "buyer") next.set("role", "buyer"); else next.delete("role");
    setSearchParams(next, { replace: true });
  };
  const { game, popBattleDocs } = useGame();
  const popBattleDoc = popBattleDocs[game] || null;
  const [region,     setRegion]     = useState("");
  const [segment,    setSegment]    = useState("");
  const [minReserve, setMinReserve] = useState("");
  const [tier,       setTier]       = useState("");
  const [language,   setLanguage]   = useState("");
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [urgency,    setUrgency]    = useState("");
  const [sort,       setSort]       = useState("newest");
  const [search,     setSearch]     = useState("");

  // results array — sellers OR buyers depending on role
  const [results, setResults] = useState([]);
  const [cursor,  setCursor]  = useState(null);
  const [done,    setDone]    = useState(false);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // Pop Battle doc for the current game — served from the shared
  // context cache (see gameContext.jsx); tab switches don't refetch
  // Firestore, and the OTHER game's assets are warmed into the
  // browser HTTP cache in the background so media reads from disk
  // on switch instead of the network.

  const filters = useMemo(() => ({
    game: game || undefined,
    region: region || undefined,
    segment: segment || undefined,
    minReserve: minReserve || undefined,
    tier: tier || undefined,
    urgency: urgency || undefined,
    sort,
  }), [game, region, segment, minReserve, tier, urgency, sort]);

  // Re-query on role/filter change. Cursor resets to null on every change.
  const fetchFirst = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = role === "buyer"
          ? await queryBuyers({ ...filters, cursor: null })
          : await querySellers({ ...filters, cursor: null });
      setResults(role === "buyer" ? res.buyers : res.sellers);
      setCursor(res.nextCursor);
      setDone(res.done);
    } catch (e) {
      console.warn(`[pop-market] query${role} failed`, e);
      setError(e.message || `Couldn't load ${role}s`);
    } finally {
      setLoading(false);
    }
  }, [filters, role]);

  useEffect(() => { fetchFirst(); }, [fetchFirst]);

  const fetchMore = async () => {
    if (done || !cursor) return;
    setLoading(true);
    try {
      const res = role === "buyer"
          ? await queryBuyers({ ...filters, cursor })
          : await querySellers({ ...filters, cursor });
      const next = role === "buyer" ? res.buyers : res.sellers;
      setResults((prev) => [...prev, ...next]);
      setCursor(res.nextCursor);
      setDone(res.done);
    } finally {
      setLoading(false);
    }
  };

  // Client-side fan-out for search + language + onlineOnly (cheap; small page).
  const visible = useMemo(() => {
    return results.filter((s) => {
      if (search) {
        const q = search.trim().toLowerCase();
        const hit = [s.handle, s.displayName, s.ign, s.city]
            .filter(Boolean)
            .some((v) => v.toLowerCase().includes(q));
        if (!hit) return false;
      }
      if (language && !(s.languages || []).includes(language)) return false;
      if (onlineOnly) {
        const last = s.lastActiveAt?.toDate?.()?.getTime?.() || 0;
        if (Date.now() - last > 15 * 60 * 1000) return false;
      }
      return true;
    });
  }, [results, search, language, onlineOnly]);

  const isBuyer = role === "buyer";
  return (
    <div className="container directory">
      <header className="dir-hero">
        <h1>
          {isBuyer
              ? "Find buyers posting popularity requirements"
              : "Find verified popularity sellers"}
        </h1>
        <p className="muted">
          {isBuyer
              ? "BGMI & PUBG Mobile buyers with active demand — quantity, max rate, deadline. Reach out off-platform."
              : "Global directory of BGMI & PUBG Mobile pop sellers — stock, rates, and reviews you can act on."}
        </p>
      </header>

      {/* Game picker — sits on the left of the page (toolbar-style)
          and governs which game's sellers/buyers appear below plus
          which Pop Battle season block shows above. */}
      <div style={{ display: "flex", justifyContent: "flex-start", marginBottom: 18 }}>
        <GamePicker />
      </div>

      {/* Popularity Battle STATUS + reg-image banner — at the top of
          the page, up year-round (the hero shows SEASON COMPLETE +
          the past battle timeline once a season ends). The notice
          below it only renders post-season. */}
      {popBattleDoc && (
        <>
          <PopBattleStatusHero game={game} doc={popBattleDoc} />
          <SeasonEndedNotice game={game} doc={popBattleDoc} />
        </>
      )}

      {/* Community feed — signed-in users post orders (qty required,
          rate optional) or freeform chat; anon visitors get a read-
          only view with a sign-in nudge. Rolling window of 100 msgs
          per game, live updates via onSnapshot, buyer/seller/both
          role badges next to each author. */}
      <ForumFeed />

      {/* Role tabs */}
      <div className="dir-role-tabs">
        <button
          className={`dir-role-tab ${role === "seller" ? "active" : ""}`}
          onClick={() => setRole("seller")}
        >
          <span className="dir-role-icon">💎</span>
          <div className="dir-role-text">
            <div className="dir-role-label">Sellers</div>
            <div className="dir-role-sub">Browse stock &amp; rates</div>
          </div>
        </button>
        <button
          className={`dir-role-tab ${role === "buyer" ? "active" : ""}`}
          onClick={() => setRole("buyer")}
        >
          <span className="dir-role-icon">🎯</span>
          <div className="dir-role-text">
            <div className="dir-role-label">Buyers</div>
            <div className="dir-role-sub">Posted requirements</div>
          </div>
        </button>
      </div>

      <ActivityStrip />

      <RateTicker game={game} />

      {/* Popularity Battle rewards video — mounted just before the
          filter row so the reg-image → tabs → activity → rate ticker
          → REWARDS → filters flow the user asked for. Renders any
          time the doc has rewards videos; the card self-hides when
          none exist, so no season gate is needed. */}
      {popBattleDoc && (
        <PopBattleRewardsCard game={game} doc={popBattleDoc} />
      )}

      {/* Points table is evergreen reference (the in-game scoring
          system) — always visible, unlike the season sections above
          which hide once a season ends. */}
      <PopBattlePointChart game={game} />
      <PopBattleLevelChart game={game} />


      <div className="dir-filters">
        <div className="dir-filters-row">
          <select value={region} onChange={(e) => setRegion(e.target.value)}>
            <option value="">Any region</option>
            {REGIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <select value={segment} onChange={(e) => setSegment(e.target.value)}>
            <option value="">Any segment</option>
            {SEGMENTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          {!isBuyer && (
            <select value={minReserve} onChange={(e) => setMinReserve(e.target.value)}>
              <option value="">Any reserves</option>
              {RESERVE_TIERS.slice(1).map((r) =>
                <option key={r.key} value={r.key}>{r.label}</option>,
              )}
            </select>
          )}
          {!isBuyer && (
            <select value={tier} onChange={(e) => setTier(e.target.value)}>
              <option value="">Any tier</option>
              {TIERS.slice(1).map((t) =>
                <option key={t.key} value={t.key}>{t.label}</option>,
              )}
            </select>
          )}
          {isBuyer && (
            <select value={urgency} onChange={(e) => setUrgency(e.target.value)}>
              <option value="">Any urgency</option>
              {URGENCIES.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
            </select>
          )}
          <select value={language} onChange={(e) => setLanguage(e.target.value)}>
            <option value="">Any language</option>
            {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <div className="dir-filters-row">
          <input
            type="search"
            placeholder="Search handle or IGN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="dir-search"
          />
          <label className="dir-toggle">
            <input
              type="checkbox"
              checked={onlineOnly}
              onChange={(e) => setOnlineOnly(e.target.checked)}
            />
            Online now
          </label>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="dir-sort">
            {(isBuyer ? BUYER_SORTS : SELLER_SORTS).map((s) =>
              <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
          </select>
        </div>
      </div>

      {error && (
        <div className="dir-error">
          {/^.*index.*(building|currently being built).*$/i.test(error) ? (
            <>
              <strong>Firestore is still building an index for this view.</strong>{" "}
              That takes 1–5 minutes after a fresh deploy.{" "}
              <button
                onClick={fetchFirst}
                style={{ marginLeft: 8, padding: "4px 12px", fontSize: 12 }}
              >Retry</button>
            </>
          ) : (
            <>{error}</>
          )}
        </div>
      )}

      {!loading && visible.length === 0 && (
        <div className="dir-empty">
          <h3>No {isBuyer ? "buyers" : "sellers"} match these filters yet.</h3>
          <p className="muted">
            Try widening your search, or post your own —{" "}
            <a href={`${import.meta.env.BASE_URL}${isBuyer ? "signup" : "list"}`}>
              {isBuyer ? "post as a buyer" : "list as a seller"}
            </a> in this slice.
          </p>
        </div>
      )}

      <div className="dir-grid">
        {visible.map((item) => isBuyer
            ? <BuyerCard key={item.handle} buyer={item} />
            : <SellerCard key={item.handle} seller={item} />)}
      </div>

      {!done && (
        <div className="dir-more">
          <button onClick={fetchMore} disabled={loading}>
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
